/**
 * Shared, pure resolution of the "primary inspection document" auto-marking rules
 * (DDDC has priority over ADRF, only before Inspection Review starts).
 *
 * Previously duplicated in the draw docs list action, the draw details load path and
 * the doc review wrapper, each with its own field names. Keeping it here means the
 * Budget Reconciliation gate sees the same primary flag no matter which screen loaded
 * the documents (AM-7277).
 */

export const PRIMARY_INSPECTION_TAG_CODES = ["DDDC", "ADRF"] as const;
export const MIN_PRIMARY_INSPECTION_CONFIDENCE = 0.9;
const NOT_STARTED_INSPECTION_STATUSES = ["", "pending"];

export type PurchaseFlagUpdate = { id: any; isPurchasePrimary: boolean };

export type AutoPrimaryInspectionResult<T> = {
  /** Document list with the auto-marked primary flags applied (new array when changed). */
  docs: T[];
  /** Payload for updatePurchaseFlags; empty when nothing changed. */
  flagUpdates: PurchaseFlagUpdate[];
};

export type AutoPrimaryInspectionOptions<T> = {
  /** Document id used by the purchase-flag API (documentId / docsId). */
  getId: (doc: T) => any;
  /** Tag list field (tags / TagsInternal). */
  getTags: (doc: T) => any[] | undefined;
  /** True once Inspection Review has started - user's manual primary is then preserved. */
  inspectionReviewStarted?: boolean;
  /**
   * Multi-document switching only runs once the draw status is known, otherwise a
   * race can flip a primary the user picked. Defaults to true.
   */
  statusLoaded?: boolean;
  /** Skip auto-marking, e.g. right after the user intentionally unmarked the primary. */
  suppressAutoPrimary?: boolean;
};

/** Reads the first tag code of a document. */
export function getPrimaryInspectionTagCode<T>(
  doc: T,
  getTags: (doc: T) => any[] | undefined
): string | undefined {
  return getTags(doc)?.[0]?.tag?.code;
}

function getConfidenceScore<T>(
  doc: T,
  getTags: (doc: T) => any[] | undefined
): number {
  return Number(getTags(doc)?.[0]?.confidence_score ?? 0);
}

/** A doc can be auto-marked only when it is extracted and confidently tagged. */
export function isAutoMarkEligible<T>(
  doc: any,
  getTags: (doc: T) => any[] | undefined
): boolean {
  const tagCode = getPrimaryInspectionTagCode(doc, getTags);
  return Boolean(
    tagCode &&
      (PRIMARY_INSPECTION_TAG_CODES as readonly string[]).includes(tagCode) &&
      doc?.assets?.length > 0 &&
      getConfidenceScore(doc, getTags) > MIN_PRIMARY_INSPECTION_CONFIDENCE
  );
}

/**
 * Inspection Review is considered started for any status other than empty/Pending.
 * Accepts the draw's budgetReconcileStatus.
 */
export function isInspectionReviewStarted(status?: string | null): boolean {
  const normalized = (status ?? "").trim().toLowerCase();
  return normalized.length > 0 && !NOT_STARTED_INSPECTION_STATUSES.includes(normalized);
}

export function filterInspectionDocs<T>(
  docList: T[],
  getTags: (doc: T) => any[] | undefined
): T[] {
  return docList.filter((doc) => {
    const tagCode = getPrimaryInspectionTagCode(doc, getTags);
    return Boolean(
      tagCode && (PRIMARY_INSPECTION_TAG_CODES as readonly string[]).includes(tagCode)
    );
  });
}

function applyFlagUpdates<T>(
  docList: T[],
  flagUpdates: PurchaseFlagUpdate[],
  getId: (doc: T) => any
): T[] {
  return docList.map((doc) => {
    const update = flagUpdates.find(
      (flag) => String(flag.id) === String(getId(doc))
    );
    return update
      ? { ...doc, isPurchasePrimary: update.isPurchasePrimary }
      : doc;
  });
}

/**
 * Resolves which inspection document should be primary.
 *
 * - Single DDDC/ADRF doc with no primary yet -> mark it primary.
 * - Multiple docs, Inspection Review not started:
 *     - no primary yet -> mark DDDC primary (DDDC wins over ADRF);
 *     - ADRF primary while an eligible DDDC exists -> switch to DDDC.
 * - Inspection Review started -> never auto-switch, the manual selection stands.
 */
export function resolveAutoPrimaryInspectionDocs<T>(
  docList: T[] | null | undefined,
  options: AutoPrimaryInspectionOptions<T>
): AutoPrimaryInspectionResult<T> {
  const docs = docList ?? [];
  const { getId, getTags, statusLoaded = true } = options;
  const empty: AutoPrimaryInspectionResult<T> = { docs, flagUpdates: [] };

  if (!docs.length || options.suppressAutoPrimary) return empty;

  const inspectionDocs = filterInspectionDocs(docs, getTags);
  if (!inspectionDocs.length) return empty;

  const existingPrimary = inspectionDocs.find(
    (doc: any) => doc?.isPurchasePrimary === true
  );

  if (inspectionDocs.length === 1) {
    const [item] = inspectionDocs;
    if (existingPrimary || !isAutoMarkEligible(item, getTags)) return empty;
    const flagUpdates = [{ id: getId(item), isPurchasePrimary: true }];
    return { docs: applyFlagUpdates(docs, flagUpdates, getId), flagUpdates };
  }

  if (!statusLoaded || options.inspectionReviewStarted) return empty;

  const dddcDoc: any = inspectionDocs.find(
    (doc) => getPrimaryInspectionTagCode(doc, getTags) === "DDDC"
  );
  if (!dddcDoc || !isAutoMarkEligible(dddcDoc, getTags)) return empty;

  if (!existingPrimary) {
    const flagUpdates = [{ id: getId(dddcDoc), isPurchasePrimary: true }];
    return { docs: applyFlagUpdates(docs, flagUpdates, getId), flagUpdates };
  }

  const adrfDoc: any = inspectionDocs.find(
    (doc) => getPrimaryInspectionTagCode(doc, getTags) === "ADRF"
  );
  if (!adrfDoc?.isPurchasePrimary || dddcDoc.isPurchasePrimary) return empty;

  const flagUpdates = [
    { id: getId(adrfDoc), isPurchasePrimary: false },
    { id: getId(dddcDoc), isPurchasePrimary: true }
  ];
  return { docs: applyFlagUpdates(docs, flagUpdates, getId), flagUpdates };
}
